import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FiAlignJustify } from "react-icons/fi";
import { MdOutlineSettings } from "react-icons/md";
import ClientSidebar from "components/clientSidebar/ClientSidebar";
import ClientNotificationDropdown from "components/client/ClientNotificationDropdown";
import SiteHeaderBanner from "components/SiteHeaderBanner";
import useSiteHeaderMessages from "hooks/useSiteHeaderMessages";
import { clearClientSession, readClientSession } from "utils/clientSession";
import { apiUrl } from "utils/apiUrl";

function formatSubscriberName(firstName, lastName) {
  const p = (firstName || "").trim();
  const n = (lastName || "").trim();
  if (p && n) return `${p} ${n}`;
  return p || n || "";
}

function titleFromPath(pathname) {
  if (pathname.includes("/client/dashboard")) return "Dashboard";
  if (pathname.includes("/client/subscription")) return "Subscription";
  if (pathname.includes("/client/referral")) return "Referral";
  if (pathname.includes("/client/contest")) return "Contest";
  if (pathname.includes("/client/billing")) return "Billing & payments";
  if (pathname.includes("/client/support/suggestions")) return "Suggestions";
  if (pathname.includes("/client/support")) return "Support & help";
  if (pathname.includes("/client/settings")) return "Settings";
  return "My account";
}

export default function ClientLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = readClientSession();
  const devGuest =
    process.env.NODE_ENV === "development" && !session;

  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const onResize = () =>
      window.innerWidth < 1200 ? setSidebarOpen(false) : setSidebarOpen(true);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [fetchedFirst, setFetchedFirst] = useState("");
  const [fetchedLast, setFetchedLast] = useState("");

  const clientId =
    session?.clientId != null && Number.isFinite(Number(session.clientId))
      ? Number(session.clientId)
      : null;

  const fromSession = useMemo(
    () =>
      formatSubscriberName(session?.firstName, session?.lastName),
    [session?.firstName, session?.lastName],
  );

  useEffect(() => {
    if (devGuest || clientId == null) {
      setFetchedFirst("");
      setFetchedLast("");
      return;
    }
    if (fromSession) {
      setFetchedFirst("");
      setFetchedLast("");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl("/api/client/profile"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clientId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !data.ok) return;
        setFetchedFirst(
          typeof data.firstName === "string" ? data.firstName : "",
        );
        setFetchedLast(typeof data.lastName === "string" ? data.lastName : "");
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [devGuest, clientId, fromSession]);

  const displayName = devGuest
    ? "Guest"
    : fromSession || formatSubscriberName(fetchedFirst, fetchedLast) || "Member";

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await fetch(apiUrl("/api/logout"), {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* ignore */
    }
    clearClientSession();
    navigate("/SignIn", { replace: true });
  };

  const pageTitle = titleFromPath(location.pathname);
  const { messages: headerMessages } = useSiteHeaderMessages("client");

  return (
    <div className="min-h-screen w-full bg-lightPrimary dark:!bg-navy-900">
      {headerMessages.length > 0 ? (
        <div className="sticky top-0 z-40">
          <SiteHeaderBanner messages={headerMessages} />
        </div>
      ) : null}
      <div className="flex min-h-[calc(100vh-0px)] w-full">
        <ClientSidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <div className="h-full min-h-screen w-full flex-1">
          <main
            className={`mx-[12px] h-full min-h-screen pb-8 transition-all md:pr-2 xl:ml-[313px]`}
          >
            <div className="sticky top-4 z-30 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white/80 px-3 py-3 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-navy-800/80">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  className="flex cursor-pointer text-xl text-navy-700 dark:text-white xl:hidden"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Open menu"
                >
                  <FiAlignJustify className="h-5 w-5" />
                </button>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-500">
                    AlphaSniper
                  </p>
                  <h1 className="truncate text-lg font-bold capitalize text-navy-700 dark:text-white">
                    {pageTitle}
                  </h1>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-2 sm:gap-x-3">
                <span
                  className="max-w-[140px] truncate text-sm font-semibold text-navy-800 dark:text-gray-100 sm:max-w-[200px]"
                  title={displayName}
                >
                  {displayName}
                </span>

                <ClientNotificationDropdown />

                <Link
                  to="/client/settings"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-navy-700 shadow-sm transition hover:bg-gray-50 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                >
                  <MdOutlineSettings className="h-4 w-4" aria-hidden />
                  Settings
                </Link>

                <button
                  type="button"
                  onClick={handleLogout}
                  className={
                    devGuest
                      ? "rounded-lg border border-brand-200 bg-brand-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600 dark:border-brand-400 dark:bg-brand-400 dark:hover:bg-brand-300"
                      : "rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-navy-700 shadow-sm transition hover:bg-gray-50 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                  }
                >
                  Log Out
                </button>
              </div>
            </div>

            <div className="px-1 pb-8 pt-1 md:px-2">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
